import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronUp, ArrowLeft, Loader2, Trash2, Edit2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CommunityMember } from "@/types";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import { AutosuggestInput } from "./AutosuggestInput";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
type CommunityVisitDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member?: CommunityMember | null;
  onVisitSaved?: () => void;
};
type VisitWithTermInfo = {
  id: string;
  date: string;
  complaint: string;
  diagnosis: string;
  temperature: number;
  treatment: string;
  requires_followup: boolean;
  term: {
    id: string;
    name: string;
    academic_year: {
      id: string;
      name: string;
    };
  };
};
export function CommunityVisitDialog({
  member,
  open,
  onOpenChange,
  onVisitSaved
}: CommunityVisitDialogProps) {
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [treatmentType, setTreatmentType] = useState<"new" | "follow-up">("new");
  const [showComplaints, setShowComplaints] = useState(true);
  const [showTemperature, setShowTemperature] = useState(true);
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [editingVisit, setEditingVisit] = useState<any>(null);

  // Form state
  const [complaint, setComplaint] = useState("");
  const [complaints, setComplaints] = useState<string[]>([]);
  const [diagnosis, setDiagnosis] = useState("");
  const [diagnoses, setDiagnoses] = useState<string[]>([]);
  const [treatment, setTreatment] = useState("");
  const [treatments, setTreatments] = useState<string[]>([]);
  const [temperature, setTemperature] = useState("36.2");
  const [requiresFollowup, setRequiresFollowup] = useState(false);

  // Query for visit history with term information
  const {
    data: visitHistory = [],
    refetch: refetchVisits
  } = useQuery({
    queryKey: ['community-visits', member?.id],
    queryFn: async () => {
      if (!member?.id) return [];
      console.log('Fetching visits for member:', member.id);
      const {
        data,
        error
      } = await supabase.from('visits').select(`
          id,
          date,
          complaint,
          diagnosis,
          temperature,
          treatment,
          requires_followup,
          term:terms!fk_active_term (
            id,
            name,
            academic_year:academic_years (
              id,
              name
            )
          )
        `).eq('community_member_id', member.id).order('date', {
        ascending: false
      });
      if (error) {
        console.error('Error fetching visits:', error);
        throw error;
      }
      console.log('Fetched visits:', data);
      return data;
    },
    enabled: !!member?.id
  });

  // Group visits by term
  const visitsByTerm = visitHistory.reduce((acc, visit) => {
    const termId = visit.term.id;
    if (!acc[termId]) {
      acc[termId] = {
        termInfo: visit.term,
        visits: []
      };
    }
    acc[termId].visits.push(visit);
    return acc;
  }, {} as Record<string, {
    termInfo: VisitWithTermInfo['term'];
    visits: VisitWithTermInfo[];
  }>);

  // Sort terms by date (most recent first)
  const sortedTerms = Object.values(visitsByTerm).sort((a, b) => {
    return new Date(b.visits[0].date).getTime() - new Date(a.visits[0].date).getTime();
  });

  // Query for nurse profile
  const {
    data: nurseProfile
  } = useQuery({
    queryKey: ['nurse-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const {
        data,
        error
      } = await supabase.from('profiles').select('id').eq('user_id', user.id).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });
  const handleEdit = (visit: any) => {
    setEditingVisit(visit);
    setComplaints(visit.complaint.split(", "));
    setDiagnoses(visit.diagnosis.split(", "));
    setTreatments(visit.treatment.split(", "));
    setTemperature(visit.temperature.toString());
    setRequiresFollowup(visit.requires_followup);
    setTreatmentType("new");
  };
  const handleDelete = async (visitId: string) => {
    if (!confirm("Are you sure you want to delete this visit record?")) return;
    try {
      const {
        error
      } = await supabase.from('visits').delete().eq('id', visitId);
      if (error) throw error;
      toast({
        title: "Success",
        description: "Visit record deleted successfully"
      });
      refetchVisits();
      queryClient.invalidateQueries({
        queryKey: ['visitStats']
      });
    } catch (error) {
      console.error('Error deleting visit:', error);
      toast({
        title: "Error",
        description: "Failed to delete visit record",
        variant: "destructive"
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member || isSubmitting || !nurseProfile?.id) return;
    setIsSubmitting(true);
    try {
      const {
        data: activeYear
      } = await supabase.from("academic_years").select("id").eq("is_active", true).maybeSingle();
      if (!activeYear?.id) {
        throw new Error("No active academic year found");
      }
      const {
        data: activeTerm
      } = await supabase.from("terms").select("id").eq("is_active", true).eq("academic_year_id", activeYear.id).maybeSingle();
      if (!activeTerm?.id) {
        throw new Error("No active term found");
      }
      const visitData = {
        community_member_id: member.id,
        complaint: treatmentType === 'follow-up' ? 'Follow-up visit' : complaints.join(", "),
        diagnosis: treatmentType === 'follow-up' ? 'Follow-up visit' : diagnoses.join(", "),
        treatment: treatments.join(", "),
        temperature: parseFloat(temperature),
        requires_followup: requiresFollowup,
        nurse_id: nurseProfile.id,
        academic_year_id: activeYear.id,
        term_id: activeTerm.id,
        school_id: user?.schoolId,
        date: new Date().toISOString()
      };
      if (editingVisit) {
        // Update existing visit
        const {
          error
        } = await supabase.from('visits').update(visitData).eq('id', editingVisit.id);
        if (error) throw error;
      } else {
        // Create new visit
        const {
          error
        } = await supabase.from('visits').insert([visitData]);
        if (error) throw error;
      }

      // Reset form
      setComplaints([]);
      setDiagnoses([]);
      setTreatments([]);
      setTemperature("36.2");
      setRequiresFollowup(false);
      setTreatmentType("new");
      setEditingVisit(null);
      onOpenChange(false);
      refetchVisits();
      onVisitSaved?.();
      toast({
        title: "Success",
        description: `Visit record ${editingVisit ? 'updated' : 'created'} successfully`
      });
    } catch (error) {
      console.error("Error saving visit:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save visit record",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl p-0">
        <div className="sticky top-0 z-50 bg-background border-b">
          <div className="px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="text-2xl font-bold">{member?.name}</h2>
              <p className="text-sm text-muted-foreground">{member?.category.replace(/_/g, ' ')}</p>
            </div>
          </div>

          <div className="px-6 pb-4">
            <Collapsible open={isContactOpen} onOpenChange={setIsContactOpen}>
              <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border bg-card p-4">
                <span className="font-medium">Contact Information</span>
                {isContactOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 px-4">
                <div className="space-y-2">
                  <div className="flex justify-between py-1">
                    <span className="text-sm font-medium">Residence:</span>
                    <span className="text-sm">{member?.residence}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-sm font-medium">Contact:</span>
                    <span className="text-sm">{member?.contact || 'Not provided'}</span>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-15rem)]">
          <div className="grid md:grid-cols-2 gap-6 p-6">
            <div className="space-y-6">
              <Card className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="treatmentType">Treatment Type</Label>
                      <Select value={treatmentType} onValueChange={(value: "new" | "follow-up") => {
                      setTreatmentType(value);
                      if (value === "follow-up") {
                        setShowComplaints(false);
                        setShowTemperature(false);
                        setShowDiagnosis(false);
                      } else {
                        setShowComplaints(true);
                        setShowTemperature(true);
                        setShowDiagnosis(false);
                      }
                    }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select treatment type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New Treatment</SelectItem>
                          <SelectItem value="follow-up">Follow-up</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Collapsible open={showComplaints} onOpenChange={setShowComplaints}>
                      <CollapsibleTrigger className="flex w-full items-center justify-between">
                        <Label>Complaints</Label>
                        <ChevronDown className={`h-4 w-4 transition-transform ${showComplaints ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className={`mt-2 ${treatmentType === 'follow-up' ? 'pointer-events-none' : ''}`}>
                          <AutosuggestInput type="COMPLAINT" value={complaint} onChange={setComplaint} onAddItem={item => setComplaints([...complaints, item])} selectedItems={complaints} onRemoveItem={item => setComplaints(complaints.filter(i => i !== item))} shortcutKey="c" />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    <Collapsible open={showTemperature} onOpenChange={setShowTemperature}>
                      <CollapsibleTrigger className="flex w-full items-center justify-between">
                        <Label>Temperature</Label>
                        <ChevronDown className={`h-4 w-4 transition-transform ${showTemperature ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className={`mt-2 ${treatmentType === 'follow-up' ? 'pointer-events-none' : ''}`}>
                          <Input id="temperature" type="number" step="0.1" value={temperature} onChange={e => setTemperature(e.target.value)} />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    <Collapsible open={showDiagnosis} onOpenChange={setShowDiagnosis}>
                      <CollapsibleTrigger className="flex w-full items-center justify-between">
                        <Label>Diagnosis</Label>
                        <ChevronDown className={`h-4 w-4 transition-transform ${showDiagnosis ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className={`mt-2 ${treatmentType === 'follow-up' ? 'pointer-events-none' : ''}`}>
                          <AutosuggestInput type="DIAGNOSIS" value={diagnosis} onChange={setDiagnosis} onAddItem={item => {
                          setDiagnoses([...diagnoses, item]);
                          setDiagnosis(''); // Clear input after adding
                        }} selectedItems={diagnoses} onRemoveItem={item => setDiagnoses(diagnoses.filter(i => i !== item))} shortcutKey="d" placeholder="Enter diagnosis (Alt + d)" />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    <div>
                      <Label>Treatment</Label>
                      <div className="mt-2">
                        <AutosuggestInput type="TREATMENT" value={treatment} onChange={setTreatment} onAddItem={item => {
                        setTreatments([...treatments, item]);
                        setTreatment(''); // Clear input after adding
                      }} selectedItems={treatments} onRemoveItem={item => setTreatments(treatments.filter(i => i !== item))} shortcutKey="t" placeholder="Enter treatment (Alt + t)" />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox id="followup" checked={requiresFollowup} onCheckedChange={checked => setRequiresFollowup(!!checked)} />
                      <label htmlFor="followup" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Requires Follow-up
                      </label>
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-[#00A651] hover:bg-[#009048] text-white" disabled={isSubmitting || (treatmentType === "new" ? !complaints.length || !diagnoses.length || !temperature : !treatments.length)}>
                    {isSubmitting ? <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {editingVisit ? 'Updating Visit...' : 'Recording Visit...'}
                      </> : editingVisit ? 'Update Visit (Alt + S)' : 'Save Visit (Alt + S)'}
                  </Button>

                  {editingVisit && <Button type="button" variant="outline" className="w-full" onClick={() => {
                  setEditingVisit(null);
                  setComplaints([]);
                  setDiagnoses([]);
                  setTreatments([]);
                  setTemperature("36.2");
                  setRequiresFollowup(false);
                  setTreatmentType("new");
                }}>
                      Cancel Edit
                    </Button>}
                </form>
              </Card>
            </div>

            {/* Visit History */}
            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Visit History</h3>
                {sortedTerms.length === 0 ? <p className="text-sm text-muted-foreground">No previous visits recorded</p> : <div className="space-y-4">
                    {sortedTerms.map(({
                  termInfo,
                  visits
                }, index) => <Collapsible key={termInfo.id} defaultOpen={index === 0}>
                        <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border bg-card p-4">
                          <div>
                            <span className="font-medium">{termInfo.name}</span>
                            <span className="text-sm text-muted-foreground ml-2">
                              ({termInfo.academic_year.name})
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {visits.length} visit{visits.length !== 1 ? 's' : ''}
                            </span>
                            <ChevronDown className="h-4 w-4 transition-transform ui-expanded:rotate-180" />
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-2">
                          <div className="space-y-4">
                            {visits.map(visit => <div key={visit.id} className="border rounded-lg p-4">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="font-medium">
                                    {format(new Date(visit.date), 'PPP')}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => handleEdit(visit)} className="text-blue-600 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-1">
                                      <Edit2 className="h-4 w-4" />
                                      <span>Edit</span>
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(visit.id)} className="text-destructive hover:bg-destructive/10 hover:text-destructive flex items-center gap-1">
                                      <Trash2 className="h-4 w-4" />
                                      <span>Delete</span>
                                    </Button>
                                    <span className="text-sm text-muted-foreground">
                                      {format(new Date(visit.date), 'p')}
                                    </span>
                                  </div>
                                </div>
                                <Separator className="my-2" />
                                <div className="space-y-2">
                                  <p className="text-sm">
                                    <span className="font-medium">Complaints:</span> {visit.complaint}
                                  </p>
                                  <p className="text-sm">
                                    <span className="font-medium">Diagnosis:</span> {visit.diagnosis}
                                  </p>
                                  <p className="text-sm">
                                    <span className="font-medium">Temperature:</span> {visit.temperature}°C
                                  </p>
                                  <p className="text-sm">
                                    <span className="font-medium">Treatment:</span> {visit.treatment}
                                  </p>
                                </div>
                              </div>)}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>)}
                  </div>}
              </Card>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>;
}